import React, {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from "react-router";
import {useTranslation} from 'react-i18next';
import {AxiosError} from 'axios';
import {Location} from 'history';
import client from 'lib/CommonApi/client';
import {
	callAlertWeb,
	callNativeWeb,
	clickMoveToAppWeb,
	footerShowEventWeb,
	hideProgressWeb,
	showProgressWeb
} from 'common/appInterface';
import Modal from '../../components/Modal';
import stringUtils from "common/stringUtils";

const ProductDetailPage: React.FC = () => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Navigate */
  const navigate = useNavigate();
  /** Params */
  const { cardProductCode } = useParams();
  /** 다국어처리 */
  const { t, i18n } = useTranslation();

  const screenId = "APPL02";
  const screenName = t("APPL02.title");

  const [productDetail, setProdcutDetail] = useState<Product>();
  const [cardApply] = useState<Partial<CardApplyStep2>>({ ...location.state.apply, cardProductCode: cardProductCode });
  const [debitState, setDebitState] = useState<CardDebitState | null>(null);
  const [isModalView, setIsModalView] = useState<boolean>(false);
  const [totalPeningFeeAmount, setTotalPeningFeeAmount] = useState<string>("0");

  /** backKeyEvent_App 처리 (APP > WEB) */
  const callbackBackkeyEvent = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /** 카드상품 조회 */
  const callProductDetail = useCallback(async () => {
    await client.get("/api/app/product/" + cardProductCode, { params: location.state.query }).then(response => {
      setProdcutDetail(response.data.response);
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    });
  }, [cardProductCode, location]);

  /** 출금동의여부조회 */
  const callDebitState = useCallback(async () => {
    await client.get('/api/app/manage/debit/state', {
      params: {
        ...location.state.query
      }
    }).then(response => {
      setDebitState(response.data.response);
    }).catch((e: AxiosError) => {
      callAlertWeb('ERROR', '99', e.message);
    });
  }, [location.state.query]);

	/** 카드신청계속 클릭 */
	const clickApplyContinue = useCallback((cardApplyNotify: CardApplyNotify) => {
		// 카드수령지 선택 단계는 수령지 선택으로 이동한다.
		if (cardApplyNotify.receiveStatusCode === "06") {
			navigate("/apply/step6/method", { state: { ...location.state, query: {...location.state.query, cardProductCode: cardApplyNotify?.cardProductCode}, apply: { ...location.state.apply, cardProductCode: cardApplyNotify?.cardProductCode, receiveSeq: cardApplyNotify.receiveSeq, receiveDate: cardApplyNotify.receiveDate, isChangeDelivery: false, designDivisionCode: cardApplyNotify.designDivisionCode }, userInfo: debitState}});
		}
		// 그 이전단계는 처음부터 시작한다.
		else {
			showProgressWeb();

			/** 고객정보 조회 */
			client.get("/api/app/apply/info", { params: {...location.state.query, cardProductCode: cardApplyNotify?.cardProductCode}}).then((response) => {
				let result = response.data.response;

				if (response.data.code === "00") {
					navigate("/apply/step0", { state: { ...location.state, query: {...location.state.query, cardProductCode: cardApplyNotify?.cardProductCode}, apply: { ...location.state.apply, cardProductCode: cardApplyNotify.cardProductCode, userEngName: result.userEngName, phoneNo: result.phoneNo, sexDivisionCode: result.sexDivisionCode, birthDate: result.birthDate, foreignerDivisionCode: result.foreignerDivisionCode }, userInfo: debitState}});
				}
				else {
					callAlertWeb("ERROR", response.data.code, response.data.message);
				}
			}).catch((e: AxiosError) => {
				callAlertWeb("ERROR", "99", e.message);
			}).finally(() => {
				hideProgressWeb();
			});
		}
	}, [navigate, location, debitState]);

  /** 카드신청 클릭 */
  const clickApply = useCallback(async () => {
		showProgressWeb();

		// 이전 접수내역 확인
		await client.get("/api/app/product/check", { params: { ...location.state.query, cardProductCode: cardProductCode }}).then(response => {
			let result = response.data.response;

			if (response.data.code === "00") {
				// 이전 접수내역이 있을 경우
				if (result.receiveStatusNotifyCode === "Y") {
					clickApplyContinue(result);
				}
				else {
					/** 고객정보 조회 */
					client.get("/api/app/apply/info", { params: { ...location.state.query, cardProductCode: cardProductCode }}).then((response) => {
						let result = response.data.response;

						if (response.data.code === "00") {
							// KYC 미인증
							if (result.kycApproved === "N") {
								callAlertWeb("ERROR", "99", t("APPL02.kycApproved"));
							}
							else {
								if (productDetail?.membershipFeeCode === '1') {
									if (result.totalPendingFeeAmount === "0") {
										navigate("/apply/step0", { state: { ...location.state, query: {...location.state.query, cardProductCode: cardProductCode}, apply: { ...cardApply, userEngName: result.userEngName, phoneNo: result.phoneNo, sexDivisionCode: result.sexDivisionCode, birthDate: result.birthDate, foreignerDivisionCode: result.foreignerDivisionCode }, userInfo: debitState}});
									} else {
										setTotalPeningFeeAmount(result.totalPendingFeeAmount);
										setIsModalView(true);
									}
								} else {
									navigate("/apply/step0", { state: { ...location.state, query: {...location.state.query, cardProductCode: cardProductCode}, apply: { ...cardApply, userEngName: result.userEngName, phoneNo: result.phoneNo, sexDivisionCode: result.sexDivisionCode, birthDate: result.birthDate, foreignerDivisionCode: result.foreignerDivisionCode }, userInfo: debitState}});
								}
							}
						}
						else {
							callAlertWeb("ERROR", response.data.code, response.data.message);
						}
					}).catch((e: AxiosError) => {
						callAlertWeb("ERROR", "99", e.message);
					});
				}
			}
			else {
				callAlertWeb("ERROR", response.data.code, response.data.message);
			}
		}).catch((e: AxiosError) => {
			callAlertWeb("ERROR", "99", e.message);
		}).finally(() => {
			hideProgressWeb();
		});
  }, [location, navigate, cardProductCode, clickApplyContinue, cardApply, t, debitState, setIsModalView, productDetail]);

  // 모달 닫기
  const closeModal = useCallback(() => {
    setIsModalView(false);
  }, []);

  // 최초실행
  useEffect(() => {
    // 언어설정
    i18n.changeLanguage(location.state.query.languageCode);
    // callNative_Web 처리 (WEB > APP)
    callNativeWeb(screenId, screenName);
    // 하단바 숨기기
    footerShowEventWeb("N");

    console.log("QUERY STATE");
    console.log(location.state);

    callProductDetail();
    callDebitState();

    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // APP이벤트 공통 수신처리 (APP > WEB)
  useEffect(() => {
    document.addEventListener("backpress", callbackBackkeyEvent);
    return () => document.removeEventListener("backpress", callbackBackkeyEvent);
  }, [callbackBackkeyEvent]);

  return (
    <div id="wrap">
      <div id="container" className="pcHidden">
        <iframe className="productDetail" src={`${process.env.REACT_APP_OUTER_HTML_PATH}/card/cardhtml/html/${location.state.query.languageCode}/${cardProductCode}_benefit.html`} allowFullScreen={true} title={productDetail?.cardProductCodeName} />
        <div className="btnArea">
          <div className="btnWrapTy01">
            <button type="button" className="btnPrimaryTy01" disabled={!productDetail || productDetail?.hasYn === "Y" || productDetail?.receiveYn === "Y"} onClick={clickApply}><span>{productDetail?.hasYn === "Y" ? t("APPL02.exist") : (productDetail?.receiveYn === "Y" ? t("APPL02.receive") : t("APPL02.apply"))}</span></button>
          </div>
        </div>
      </div>
      <Modal isOpen={isModalView} onRequestClose={closeModal} className="popTyLayer2" overlayClassName="dim" closeTimeoutMS={500}>
        <div>
          <div className='Please-pay-Pending-Fee'>
            <span>{t('APPL02.pendingFee').replace('{0}', stringUtils.nvlDefault(stringUtils.moneyFormat(totalPeningFeeAmount)))}</span>
          </div>
        </div>
        <div className="btnWrapTy02">
          <button type="button" className="btnPrimaryTy01" onClick={() => clickMoveToAppWeb("N039")}>
            <span>{t("SERV16.button.pay")}</span>
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ProductDetailPage;